import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api/axios";
import { AuthContext } from "../App";

const FeedbackForm = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRTL = i18n.language === 'ar';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      toast.error(t('feedback.emptyError'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.post('/feedback', {
        message: feedback.trim(),
        user_email: user?.email || 'anonymous',
        app_language: i18n.language,
        timestamp: new Date().toISOString()
      });
      
      toast.success(t('feedback.successMessage'));
      setFeedback("");
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(t('feedback.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#10b981]" />
        <h3 className="text-lg font-semibold text-ocean">{t('feedback.title')}</h3>
      </div>
      
      <p className="text-sm text-slate-600 mb-4">
        {t('feedback.description')}
      </p>
      
      <form onSubmit={handleSubmit}>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={t('feedback.placeholder')}
          className={`w-full h-32 bg-slate-50 border-2 border-slate-200 focus:border-[#10b981] focus:ring-4 focus:ring-[#10b981]/10 rounded-xl p-4 resize-none transition-all text-sm ${isRTL ? 'text-right' : 'text-left'}`}
          dir={isRTL ? 'rtl' : 'ltr'}
          disabled={isSubmitting}
          data-testid="feedback-textarea"
        />
        
        <button
          type="submit"
          disabled={isSubmitting || !feedback.trim()}
          className="mt-4 w-full bg-gradient-to-r from-[#10b981] to-[#0EA5E9] text-white rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-[#10b981]/25"
          data-testid="feedback-submit-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{t('feedback.sending')}</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>{t('feedback.submitButton')}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default FeedbackForm;
